import type { KeyboardEvent } from 'react';

import type { InputValue } from '../../Input/types';
import type { CompoundInputSegment, CompoundInputValue } from '../types';

import { Input } from '../../Input/Input';
import { Select } from '../../Select/Select';
import { withSegmentValue } from '../utils';

type CompoundInputSegmentControlProps<V extends CompoundInputValue> = {
  segment: CompoundInputSegment<V>;
  value: V;
  onChange: (value: V) => void;
  disabled?: boolean;
  required?: boolean;
  /** Ignored for `select` segments — `Select` has no public `onKeyDown`. */
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  /** Forwarded only to `select` segments, to floor a "hug" trigger's open menu. */
  menuMinWidth?: number;
  __private: {
    ariaDescribedBy?: string;
    id: string;
  };
};

export function CompoundInputSegmentControl<V extends CompoundInputValue>({
  segment,
  value,
  onChange,
  disabled,
  required,
  onKeyDown,
  menuMinWidth,
  __private,
}: CompoundInputSegmentControlProps<V>) {
  const { label, name, placeholder } = segment;
  const isDisabled = disabled || segment.disabled;
  // `role="group"` takes no `aria-required`, so a required compound field marks
  // both of its widgets instead of the container.
  const isRequired = required || segment.required;

  // `invalid` carries no visuals of its own: `seamless` strips whatever
  // `status` would paint on the segment, leaving only `aria-invalid` behind.
  const status = segment.invalid ? 'critical' : undefined;

  const controlPrivate = { ...__private, seamless: true };

  if (segment.type === 'select') {
    return (
      <Select<unknown>
        disabled={isDisabled}
        label={label}
        menuMinWidth={menuMinWidth}
        multiple={false}
        name={name}
        noResultsText={segment.noResultsText}
        options={segment.options}
        placeholder={placeholder}
        required={isRequired}
        status={status}
        value={value[name] ?? null}
        onChange={(next) => onChange(withSegmentValue(value, name, next))}
        __private={controlPrivate}
      />
    );
  }

  return (
    <Input<'number' | 'text'>
      disabled={isDisabled}
      label={label}
      name={name}
      placeholder={placeholder}
      required={isRequired}
      status={status}
      type={segment.type}
      // The segment union ties `type` to the value's type, but indexing the
      // composite by an erased key loses that link.
      value={value[name] as InputValue<number | string>}
      onChange={(next) => onChange(withSegmentValue(value, name, next))}
      onKeyDown={onKeyDown}
      __private={controlPrivate}
    />
  );
}
